import { getMessaging, getToken, deleteToken, isSupported, onMessage, Messaging } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { app, db, auth } from './firebase';

/**
 * Push értesítések (FCM) + PWA service worker.
 *
 * - A service worker (public/firebase-messaging-sw.js) az offline app-shellt és a háttér-pusht kezeli.
 * - A push tokent a `pushTokens/{token}` dokumentum tárolja (userId, organizationId, eszköz), a Cloud Function
 *   (functions/push.js) az új `notifications` bejegyzésekhez keresi ki és küldi el.
 * - VAPID kulcs: REACT_APP_FIREBASE_VAPID_KEY (Firebase Console → Cloud Messaging → Web Push certificates).
 */

const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';
const SW_PATH = '/firebase-messaging-sw.js';
const TOKEN_KEY = 'youcrm.pushToken';

const swUrl = () => {
  const cfg = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  };
  return `${SW_PATH}?cfg=${btoa(JSON.stringify(cfg))}`;
};

let registration: ServiceWorkerRegistration | null = null;

/** Service worker regisztrálása (PWA + push). Éles buildben és localhost-on fut; hiba esetén csendben kihagy. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register(swUrl(), { scope: '/' });
    // Új verzió: azonnal aktiválódjon (a fetch-handler cache-verzió miatt)
    registration.addEventListener('updatefound', () => {
      const nw = registration?.installing;
      nw?.addEventListener('statechange', () => { if (nw.state === 'installed' && navigator.serviceWorker.controller) nw.postMessage({ type: 'SKIP_WAITING' }); });
    });
    return registration;
  } catch (e) {
    console.warn('[push] service worker regisztráció sikertelen', e);
    return null;
  }
}

export type PushStatus = 'unsupported' | 'no-vapid' | 'denied' | 'default' | 'enabled' | 'disabled';

/** Aktuális állapot a beállítások oldalhoz. */
export async function getPushStatus(): Promise<PushStatus> {
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  if (!(await isSupported().catch(() => false))) return 'unsupported';
  if (!VAPID) return 'no-vapid';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'default';
  return localStorage.getItem(TOKEN_KEY) ? 'enabled' : 'disabled';
}

const deviceLabel = () => {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'egyéb';
  const br = ua.includes('Edg/') ? 'Edge' : ua.includes('Chrome/') ? 'Chrome' : ua.includes('Firefox/') ? 'Firefox' : ua.includes('Safari/') ? 'Safari' : 'böngésző';
  const pwa = window.matchMedia('(display-mode: standalone)').matches ? ' · telepített app' : '';
  return `${br} · ${os}${pwa}`;
};

let messagingInstance: Messaging | null = null;
const messaging = () => messagingInstance || (messagingInstance = getMessaging(app));

/** Push bekapcsolása ezen az eszközön: engedélykérés → token → mentés. */
export async function enablePush(organizationId?: string | null): Promise<{ ok: boolean; reason?: string }> {
  const status = await getPushStatus();
  if (status === 'unsupported') return { ok: false, reason: 'Ez a böngésző nem támogatja a push értesítést. iPhone-on előbb tedd a kezdőképernyőre (Megosztás → Kezdőképernyőhöz adás), és onnan nyisd meg.' };
  if (status === 'no-vapid') return { ok: false, reason: 'Hiányzik a VAPID kulcs (REACT_APP_FIREBASE_VAPID_KEY).' };
  const user = auth.currentUser;
  if (!user) return { ok: false, reason: 'Nincs bejelentkezve.' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'A böngésző nem engedélyezte az értesítéseket – a címsor lakat ikonjánál engedélyezhető.' };
  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'A service worker nem indult el.' };
  await navigator.serviceWorker.ready;
  try {
    const token = await getToken(messaging(), { vapidKey: VAPID, serviceWorkerRegistration: reg });
    if (!token) return { ok: false, reason: 'Nem sikerült push tokent kérni.' };
    await setDoc(doc(db, 'pushTokens', token), {
      token, userId: user.uid, organizationId: organizationId || null,
      device: deviceLabel(), userAgent: navigator.userAgent.slice(0, 200),
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(), lastSeenAt: serverTimestamp()
    }, { merge: true });
    localStorage.setItem(TOKEN_KEY, token);
    return { ok: true };
  } catch (e: any) {
    console.error('[push] token hiba', e);
    return { ok: false, reason: e?.message || 'Push token hiba' };
  }
}

/** Push kikapcsolása ezen az eszközön. */
export async function disablePush(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  try { if (token) await deleteDoc(doc(db, 'pushTokens', token)); } catch { /* ignore */ }
  try { await deleteToken(messaging()); } catch { /* ignore */ }
  localStorage.removeItem(TOKEN_KEY);
}

/** Belépés után: ha ezen az eszközön be van kapcsolva, frissítjük a tokent (lejárat / user-váltás). */
export async function refreshPushToken(organizationId?: string | null): Promise<void> {
  try {
    if (!localStorage.getItem(TOKEN_KEY) || Notification.permission !== 'granted' || !VAPID) return;
    const user = auth.currentUser; if (!user) return;
    const reg = await registerServiceWorker(); if (!reg) return;
    const token = await getToken(messaging(), { vapidKey: VAPID, serviceWorkerRegistration: reg });
    if (!token) return;
    const old = localStorage.getItem(TOKEN_KEY);
    if (old && old !== token) { try { await deleteDoc(doc(db, 'pushTokens', old)); } catch { /* ignore */ } }
    await setDoc(doc(db, 'pushTokens', token), { token, userId: user.uid, organizationId: organizationId || null, device: deviceLabel(), updatedAt: serverTimestamp(), lastSeenAt: serverTimestamp() }, { merge: true });
    localStorage.setItem(TOKEN_KEY, token);
  } catch (e) { console.warn('[push] token frissítés', e); }
}

/** Kilépéskor: a token ne szóljon másnak, aki ezen az eszközön belép. */
export async function detachPushOnLogout(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;
  try { await deleteDoc(doc(db, 'pushTokens', token)); } catch { /* ignore */ }
  // a localStorage jelzőt megtartjuk: a következő belépéskor refreshPushToken újra beregisztrálja az új usernek
}

/** Előtérben érkező push: nem mutatunk semmit – az app maga (onSnapshot) jelzi. */
export function listenForegroundPush(): void {
  try { onMessage(messaging(), () => { /* in-app kezelés */ }); } catch { /* ignore */ }
}
